import type { ComponentType, ReactNode } from "react";

export interface LegalSection {
  id: string;
  title: string;
  body: ReactNode;
}

/** Enterprise legal-page shell: gradient hero, sticky table of contents,
 *  anchored sections, entity footer. Content comes in as data. */
export function LegalPage({
  icon: Icon,
  eyebrow,
  title,
  updated,
  intro,
  sections,
}: {
  icon: ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  updated: string;
  intro: ReactNode;
  sections: LegalSection[];
}) {
  return (
    <div>
      {/* Hero — dark, matching the site header */}
      <div className="relative overflow-hidden border-b border-white/10 bg-slate-950">
        <div className="pointer-events-none absolute inset-0 bg-aurora opacity-70" />
        <div className="pointer-events-none absolute -left-24 -top-16 h-72 w-72 rounded-full bg-accent-600/25 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-6 py-16">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-glow">
              <Icon className="h-6 w-6" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-accent-300">{eyebrow}</p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-white sm:text-4xl">{title}</h1>
              <p className="mt-2 text-sm text-slate-400">Last updated: {updated}</p>
            </div>
          </div>
          <div className="mt-6 max-w-3xl text-[15px] leading-relaxed text-slate-300">{intro}</div>
        </div>
      </div>

      {/* Body: sticky TOC + sections */}
      <div className="mx-auto max-w-6xl gap-12 px-6 py-14 lg:grid lg:grid-cols-[240px_1fr]">
        <nav className="mb-10 lg:mb-0">
          <div className="lg:sticky lg:top-24">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">On this page</p>
            <ol className="mt-3 space-y-2 border-l border-slate-200 text-sm">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="-ml-px block border-l-2 border-transparent py-0.5 pl-4 text-slate-500 transition hover:border-brand-400 hover:text-slate-900"
                  >
                    <span className="mr-1.5 font-mono text-[11px] text-slate-300">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </nav>

        <div className="min-w-0 space-y-12">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="flex items-baseline gap-3 text-xl font-bold tracking-tight text-slate-900">
                <span className="font-mono text-sm font-semibold text-brand-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {s.title}
              </h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600 [&_li]:ml-5 [&_li]:list-disc [&_strong]:text-slate-800">
                {s.body}
              </div>
            </section>
          ))}

          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-6 py-5 text-xs leading-relaxed text-slate-500">
            Wise 2.0 is operated by <strong className="text-slate-700">Shineywise Technologies OPC Pvt.&nbsp;Ltd.</strong>{" "}
            and <strong className="text-slate-700">Shineywise Technologies LLC</strong> (together, "Shineywise").
            Questions about this document:{" "}
            <a href="mailto:info@shineywise.com" className="font-medium text-brand-700 hover:underline">
              info@shineywise.com
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
